import React from 'react';
import { Game } from 'app-types';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import AuthNWrapper, { WithAuthNProps } from 'components/hoc/AuthNWrapper';

import { getVodTime } from 'support/mediaTileHelper';
import { getDurationTTS } from 'support/textToSpeechHelper';
import { ChannelKey } from 'app-types';
import MediaTile from './MediaTile';
import BrandedAuthTile from './BrandedAuthTile';

import styles from './VodTile.module.scss';
import HighlightsTile from './HighlightsTile';

const messages = {
  nowPlaying: {
    id: 'tile.nowPlaying',
    description: 'Text for now playing video flag',
  },
  authText: {
    id: 'tile.auth.text',
    description: 'Auth text for Media Tile',
  },
};

interface VodTileOwnProps {
  title?: string | null;
  duration?: number | null;
  isNowPlaying?: boolean;
  brand?: ChannelKey | null;
  displayScoring?: boolean;
  game?: Game | null;
  src?: string | null;
  focusId?: string | number | null;
  className?: string | null;
  onClick?: () => void;
  onFocusSet?: () => void;
}

type VodTileProps = VodTileOwnProps & WithAuthNProps;

const VodTile = ({
  title = null,
  duration = null,
  isNowPlaying = false,
  authenticated = false,
  authTypes = [],
  brand = null,
  displayScoring = false,
  game = null,
  ...rest
}: VodTileProps) => {
  const intl = useIntl();

  const renderTitle = () => (
    <div className={classNames('text-truncate', styles.title)}>{title}</div>
  );

  const renderMetadata = (hasFocus: boolean) => {
    const time = getVodTime(duration as number);
    if (time === '') return null;
    return (
      <>
        <BrandedAuthTile
          hasFocus={hasFocus}
          brand={brand}
          authenticated={authenticated}
          authTypes={authTypes}
        />

        <div className={styles.time} data-testid="metadata">
          {time}
        </div>
      </>
    );
  };

  const tts = [
    isNowPlaying ? intl.formatMessage(messages.nowPlaying) : null,
    !authenticated ? intl.formatMessage(messages.authText) : null,
    title,
    getDurationTTS(duration as number, intl),
  ]
    .filter(item => item)
    .join(', ');

  let result;
  if (displayScoring) {
    result = (
      <HighlightsTile
        renderMetadata={renderMetadata}
        size="md"
        isNowPlaying={isNowPlaying}
        game={game}
        ttsText={tts}
        {...rest}
      />
    );
  } else {
    result = (
      <MediaTile
        renderTitle={renderTitle}
        renderMetadata={renderMetadata}
        size="md"
        isNowPlaying={isNowPlaying}
        ttsText={tts}
        {...rest}
      />
    );
  }

  return result;
};

export default React.memo(AuthNWrapper(VodTile));
